import { Injectable } from '@angular/core';

/**
 * Cookie Service for managing user preferences and settings
 * Stores preferences that persist across login sessions
 */
@Injectable({ providedIn: 'root' })
export class CookieService {
  private readonly DOMAIN_PATH = '/';
  private readonly MAX_AGE_SECONDS = 365 * 24 * 60 * 60; // 1 year in seconds

  /**
   * Set a cookie with the given name, value, and optional expiry
   * @param name Cookie name
   * @param value Cookie value
   * @param maxAgeDays Days until cookie expires (default: 365)
   */
  setCookie(name: string, value: string, maxAgeDays: number = 365): void {
    const maxAge = maxAgeDays * 24 * 60 * 60; // Convert days to seconds
    const cookieString = `${name}=${encodeURIComponent(value)}; path=${this.DOMAIN_PATH}; max-age=${maxAge}`;
    document.cookie = cookieString;
  }

  /**
   * Get a cookie by name
   * @param name Cookie name
   * @returns Cookie value or null if not found
   */
  getCookie(name: string): string | null {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [cookieName, cookieValue] = cookie.trim().split('=');
      if (cookieName === name) {
        return cookieValue ? decodeURIComponent(cookieValue) : null;
      }
    }
    return null;
  }

  /**
   * Remove a cookie by name
   * @param name Cookie name
   */
  removeCookie(name: string): void {
    document.cookie = `${name}=; path=${this.DOMAIN_PATH}; max-age=0`;
  }

  /**
   * Get all cookies as an object
   * @returns Object with cookie names as keys and values as cookie values
   */
  getAllCookies(): { [key: string]: string } {
    const cookies: { [key: string]: string } = {};
    document.cookie.split(';').forEach((cookie) => {
      const [name, value] = cookie.trim().split('=');
      if (name && value) {
        cookies[name] = decodeURIComponent(value);
      }
    });
    return cookies;
  }

  /**
   * Check if a cookie exists
   * @param name Cookie name
   * @returns True if cookie exists, false otherwise
   */
  hasCookie(name: string): boolean {
    return this.getCookie(name) !== null;
  }

  /**
   * Set a boolean preference cookie
   * @param name Cookie name
   * @param value Boolean value
   * @param maxAgeDays Days until cookie expires (default: 365)
   */
  setBooleanCookie(name: string, value: boolean, maxAgeDays: number = 365): void {
    this.setCookie(name, value ? 'true' : 'false', maxAgeDays);
  }

  /**
   * Get a boolean preference cookie
   * @param name Cookie name
   * @param defaultValue Default value if cookie doesn't exist
   * @returns Boolean value
   */
  getBooleanCookie(name: string, defaultValue: boolean = false): boolean {
    const value = this.getCookie(name);
    if (value === null) {
      return defaultValue;
    }
    return value === 'true';
  }

  /**
   * Set a JSON preference cookie
   * @param name Cookie name
   * @param value Object to serialize
   * @param maxAgeDays Days until cookie expires (default: 365)
   */
  setJsonCookie(name: string, value: any, maxAgeDays: number = 365): void {
    try {
      this.setCookie(name, JSON.stringify(value), maxAgeDays);
    } catch (error) {
      console.error(`Failed to set JSON cookie '${name}':`, error);
    }
  }

  /**
   * Get a JSON preference cookie
   * @param name Cookie name
   * @param defaultValue Default value if cookie doesn't exist
   * @returns Parsed object or default value
   */
  getJsonCookie(name: string, defaultValue: any = null): any {
    const value = this.getCookie(name);
    if (value === null) {
      return defaultValue;
    }
    try {
      return JSON.parse(value);
    } catch (error) {
      console.error(`Failed to parse JSON cookie '${name}':`, error);
      return defaultValue;
    }
  }
}
